import { useCallback } from "react";

// region External libraries
import { DocumentNode, OperationVariables, useMutation } from "@apollo/client";
import { useToast } from "@npm-telluria-tecnologia/telluria-ui";
// endregion External libraries
// region Store
import { ECommandResultStatus } from "src/store/graphql/helpers/types.graphql";
import { CommandResultPayload } from "src/store/graphql/helpers/commandResult.graphql";
// endregion Store

export interface CommandMutationOptions {
  /** Skips the success toast (e.g. silent interaction logging). */
  silent?: boolean;
  /** Runs after a SUCCESS result, typically a refetch. */
  onSuccess?: () => void | Promise<unknown>;
}

export interface CommandMutationState {
  /** Runs the mutation; resolves true on a SUCCESS command result. */
  run: (variables?: OperationVariables) => Promise<boolean>;
  loading: boolean;
}

/**
 * Runs a GraphQL mutation that resolves to the library CommandResult shape
 * ({ status, message }): toasts the message on SUCCESS, runs the follow-up
 * and reports whether it succeeded. Network/GraphQL errors are already
 * toasted by the Apollo error link, so they only resolve false.
 */
export const useCommandMutation = <TData>(
  document: DocumentNode,
  pick: (data: TData) => CommandResultPayload | undefined,
  { silent = false, onSuccess }: CommandMutationOptions = {}
): CommandMutationState => {

  const [mutate, { loading }] = useMutation<TData>(document);
  const { addToast } = useToast();

  const run = useCallback(async (variables?: OperationVariables) => {
    try {
      const { data } = await mutate({ variables });
      const payload = data ? pick(data) : undefined;

      if (payload?.status !== ECommandResultStatus.SUCCESS) return false;

      if (!silent) addToast({ type: "success", title: payload.message });
      await onSuccess?.();

      return true;
    } catch (error) {
      return false;
    }
  }, [mutate, pick, silent, onSuccess, addToast]);

  return { run, loading };
};
